import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLessonSession } from '../../contexts/LessonSessionContext';
import './LessonReturnPill.css';

/**
 * "Back to Sarah's lesson" — the way back into an open lesson from anywhere
 * else in the portal.
 *
 * The other half of the answer to Anthony's 2026-09-02 review: leaving a lesson
 * is now non-destructive (LessonSessionContext + useLessonScratch), and this
 * makes it FINDABLE again. Without it a teacher who navigated away has no route
 * back except the browser's back button — which the installed PWA does not have
 * (the same gap that put a space switcher in this topbar on 2026-08-03).
 *
 * Deliberately NOT hover-revealed and not icon-only: the portal's primary user
 * has keratoconus and the PWA has no hover, so this is always rendered, carries
 * its student's name as text, and is quieted with tokens rather than opacity.
 *
 * Self-hiding: nothing renders unless a lesson is open AND you are somewhere
 * else. Standing on the lesson itself, it would be noise.
 */
const LessonReturnPill = () => {
	const { openLesson, clearOpenLesson } = useLessonSession();
	const { pathname } = useLocation();

	if (!openLesson) return null;

	const target = `/portal/work/lesson/${openLesson.lessonId}`;
	if (pathname === target) return null;

	const first = (openLesson.studentName || '').trim().split(/\s+/)[0];
	const label = first ? `Back to ${first}'s lesson` : 'Back to the lesson';

	return (
		<span className="portal-lessonback">
			<Link to={target} className="portal-lessonback__link" title={label}>
				<i className="fa-sharp fa-solid fa-chalkboard-user" aria-hidden="true" />
				<span className="portal-lessonback__text">{label}</span>
			</Link>
			<button
				type="button"
				className="portal-lessonback__close"
				aria-label="Stop showing this lesson"
				title="Stop showing this lesson"
				onClick={clearOpenLesson}>
				<i className="fa-sharp fa-solid fa-xmark" aria-hidden="true" />
			</button>
		</span>
	);
};

export default LessonReturnPill;
